import React, { lazy, Suspense } from 'react'
import { Layers, Boxes, Clock, Sparkles } from 'lucide-react'
import type { LeftPanelMode, CmsEditorSidebarProps } from './types'
import LayersPanel from '@/components/cms/LayersPanel'
import ComponentDrawer from '@/components/cms/ComponentDrawer'
import HistoryPanel from '@/components/cms/HistoryPanel'

const AiGeneratorPanel = lazy(() => import('@/components/cms/AiGeneratorPanel'))

/* ── Tab definitions ─────────────────────────────────── */

const LEFT_TABS: { mode: LeftPanelMode; icon: React.ReactNode; label: string }[] = [
  { mode: 'layers', icon: <Layers size={13} />, label: 'Layers' },
  { mode: 'components', icon: <Boxes size={13} />, label: 'Components' },
  { mode: 'history', icon: <Clock size={13} />, label: 'History' },
  { mode: 'ai', icon: <Sparkles size={13} />, label: 'AI' },
]

/* ── Component ───────────────────────────────────────── */

export default function CmsEditorSidebar({
  sidebarWidth,
  leftPanelMode,
  canvasBlocks,
  selectedBlockId,
  lockedBlockIds,
  tenantId,
  onSetLeftPanelMode,
  onAddBlock,
  onAddPreset,
  onSelectBlock,
  onRemoveBlock,
  onDuplicateBlock,
  onReorderBlocks,
  onToggleBlockLock,
  onSaveTemplate,
  onResizeStart,
  templates,
  historySnapshots,
  historyIndex,
  onRestoreSnapshot,
  onNameSnapshot,
}: CmsEditorSidebarProps) {
  return (
    <aside
      className="editor-panel"
      style={{
        width: sidebarWidth,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        borderRight: '1px solid var(--editor-border)',
      }}
    >
      {/* Tab bar */}
      <div className="editor-tab-bar">
        {LEFT_TABS.map(({ mode, icon, label }) => (
          <button
            key={mode}
            className="editor-tab"
            data-active={leftPanelMode === mode}
            onClick={() => onSetLeftPanelMode(mode)}
            title={label}
          >
            {icon}
            <span>{label}</span>
          </button>
        ))}
      </div>

      {/* Panel content */}
      <div style={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {leftPanelMode === 'layers' && (
          <LayersPanel
            blocks={canvasBlocks}
            selectedBlockId={selectedBlockId}
            lockedBlockIds={lockedBlockIds}
            onSelect={onSelectBlock}
            onRemove={onRemoveBlock}
            onDuplicate={onDuplicateBlock}
            onReorder={onReorderBlocks}
            onToggleLock={onToggleBlockLock}
          />
        )}

        {leftPanelMode === 'components' && (
          <ComponentDrawer
            blocks={canvasBlocks}
            onAddBlock={onAddBlock}
            onAddPreset={onAddPreset}
            templates={templates}
            onSaveTemplate={onSaveTemplate}
          />
        )}

        {leftPanelMode === 'history' && (
          <HistoryPanel
            snapshots={historySnapshots}
            currentIndex={historyIndex}
            onRestore={onRestoreSnapshot}
            onNameSnapshot={onNameSnapshot}
          />
        )}

        {leftPanelMode === 'ai' && (
          <Suspense fallback={<div style={{ padding: 16, textAlign: 'center', fontSize: 11, opacity: 0.4 }}>Loading…</div>}>
            <AiGeneratorPanel
              tenantId={tenantId}
              canvasBlocks={canvasBlocks}
              onAddBlock={onAddBlock}
              onAddPreset={onAddPreset}
            />
          </Suspense>
        )}
      </div>

      {/* Resize handle */}
      <div className="editor-split-handle" onMouseDown={onResizeStart} style={{ position: 'absolute', right: 0, top: 0, bottom: 0 }} />
    </aside>
  )
}
